// AdminDashboard.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { callLarkProxy, LARK_TABLES, discoverBase } from "./larkService_v2";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx";

function AdminDashboard() {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, top, low
  const [searchText, setSearchText] = useState("");
  const [larkError, setLarkError] = useState("");
  const [debugData, setDebugData] = useState(null);

  useEffect(() => {
    const loadInterviews = async () => {
      try {
        const result = await callLarkProxy("GET", `/tables/${LARK_TABLES.INTERVIEWS}/records?page_size=500`);
        setDebugData(result);
        if (result.data?.items) {
          const mapped = result.data.items.map(item => ({
            id: item.record_id,
            candidateName: item.fields["Candidate Name"] || item.fields["candidateName"],
            userId: item.fields["User ID"] || item.fields["userId"],
            positionTitle: item.fields["Position Title"] || item.fields["positionTitle"],
            resumeScore: Number(item.fields["Resume Score"] || item.fields["resumeScore"]) || 0,
            interviewScore: Number(item.fields["Interview Score"] || item.fields["interviewScore"]) || 0,
            finalScore: Number(item.fields["Final Score"] || item.fields["finalScore"]) || 0,
            status: item.fields["Status"] || item.fields["status"],
            answers: item.fields["Answers JSON"] ? JSON.parse(item.fields["Answers JSON"]) : [],
            resumeUrl: item.fields["Resume URL"] || item.fields["resumeUrl"],
            videoUrl: item.fields["Video URL"] || item.fields["videoUrl"],
            cheatingFlags: item.fields["Cheating Flags"] || item.fields["cheatingFlags"],
            completedAt: item.fields["Completed At"] || item.fields["completedAt"],
            excelData: item.fields["Excel Data"] || item.fields["excelData"],
            excelVideoUrl: item.fields["Excel Video"] || item.fields["Excel Video URL"] || item.fields["excelVideoUrl"],
          }));
          // Sort descending by ID (which is sequential in Lark) and then by date
          mapped.sort((a, b) => {
            // Lark record IDs are strings like rec... but generally newer ones are "greater" or we can use completedAt fallback
            const timeA = a.completedAt ? new Date(a.completedAt).getTime() : 0;
            const timeB = b.completedAt ? new Date(b.completedAt).getTime() : 0;
            
            if (timeA !== timeB) return timeB - timeA;
            return b.id.localeCompare(a.id); // Fallback to ID sorting
          });
          setInterviews(mapped);
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching interviews:", err);
        setLarkError(err.message || JSON.stringify(err));
        setLoading(false);
      }
    };
    loadInterviews();
  }, []);

  // Filter logic
  const filteredInterviews = interviews.filter((iv) => {
    const matchesFilter =
      filter === "all" ||
      (filter === "top" && (iv.finalScore || 0) >= 7) ||
      (filter === "low" && (iv.finalScore || 0) < 5);
    const matchesSearch =
      !searchText ||
      (iv.candidateName || "").toLowerCase().includes(searchText.toLowerCase()) ||
      (iv.userId || "").toLowerCase().includes(searchText.toLowerCase()) ||
      (iv.resumeFileName || "").toLowerCase().includes(searchText.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Stats
  const totalInterviews = interviews.length;
  const avgFinalScore = interviews.length > 0
    ? (interviews.reduce((sum, iv) => sum + (Number(iv.finalScore) || 0), 0) / interviews.length).toFixed(1)
    : "0";
  const topCandidates = interviews.filter((iv) => (iv.finalScore || 0) >= 7).length;
  const lowCandidates = interviews.filter((iv) => (iv.finalScore || 0) < 5).length;

  const handleDelete = async (id) => {
    if (window.confirm("⚠️ Are you sure you want to permanently delete this candidate record? This action cannot be undone.")) {
      try {
        await callLarkProxy("DELETE", `/tables/${LARK_TABLES.INTERVIEWS}/records/${id}`);
        setInterviews(prev => prev.filter(iv => iv.id !== id));
        toast.success("Record deleted successfully.");
      } catch (err) {
        console.error("Delete error:", err);
        toast.error("Failed to delete record.");
      }
    }
  };

  const getScoreColor = (score) => {
    if (score >= 7) return "#10b981";
    if (score >= 4) return "#f59e0b";
    return "#ef4444";
  };

  const getScoreBadge = (score) => {
    if (score >= 7) return "Good";
    if (score >= 4) return "Average";
    return "Needs Improvement";
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  const handleDownloadExcel = (iv) => {
    if (!iv.excelData) {
      toast.error("No Excel data found for this candidate.");
      return;
    }
    try {
      const parsedData = JSON.parse(iv.excelData);
      const wb = XLSX.utils.book_new();
      
      parsedData.forEach((sheet, index) => {
        const sheetName = sheet.name || `Sheet${index + 1}`;
        // FortuneSheet structure: celldata is an array of {r, c, v: {v, m}} etc.
        // We will map it to a simple 2D array:
        const aoa = [];
        if (sheet.celldata) {
           sheet.celldata.forEach(cell => {
             const r = cell.r;
             const c = cell.c;
             const v = cell.v ? (cell.v.v || cell.v.m || "") : "";
             if (!aoa[r]) aoa[r] = [];
             aoa[r][c] = v;
           });
           
           // Fill empty holes so XLSX doesn't break
           for(let i=0; i<aoa.length; i++) {
             if (!aoa[i]) aoa[i] = [];
           }
        }
        
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      XLSX.writeFile(wb, `Excel_Test_${iv.userId || "candidate"}.xlsx`);
      toast.success("Excel file downloaded!");
    } catch(e) {
      console.error("Failed to parse excel data:", e);
      toast.error("Error generating Excel file.");
    }
  };

  return (
    <div className="admin-dashboard-page">
      <div className="admin-container">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-header-left">
            <h1>
              <span className="header-icon">👥</span>
              Candidate Dashboard
            </h1>
            <p>Manage and review all candidate interviews</p>
          </div>
          <button className="back-btn" onClick={() => navigate("/dashboard")}>
            ← Back to Dashboard
          </button>
        </div>


        {larkError && (
          <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "16px", borderRadius: "8px", margin: "20px 0", border: "1px solid #f87171" }}>
            <strong>Lark Connection Error:</strong> {larkError}
          </div>
        )}

        {/* Stats Cards */}
        <div className="admin-stats">
          <div className="admin-stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <span className="stat-number">{totalInterviews}</span>
              <span className="stat-label">Total Interviews</span>
            </div>
          </div>
          <div className="admin-stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-content">
              <span className="stat-number">{avgFinalScore}</span>
              <span className="stat-label">Avg Score</span>
            </div>
          </div>
          <div className="admin-stat-card highlight-green">
            <div className="stat-icon">🏆</div>
            <div className="stat-content">
              <span className="stat-number">{topCandidates}</span>
              <span className="stat-label">Top Candidates</span>
            </div>
          </div>
          <div className="admin-stat-card highlight-red">
            <div className="stat-icon">⚠️</div>
            <div className="stat-content">
              <span className="stat-number">{lowCandidates}</span>
              <span className="stat-label">Low Score</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="admin-filters">
          <div className="filter-tabs">
            <button
              className={`filter-tab ${filter === "all" ? "active" : ""}`}
              onClick={() => setFilter("all")}
            >
              All ({totalInterviews})
            </button>
            <button
              className={`filter-tab top ${filter === "top" ? "active" : ""}`}
              onClick={() => setFilter("top")}
            >
              🏆 Top Candidates ({topCandidates})
            </button>
            <button
              className={`filter-tab low ${filter === "low" ? "active" : ""}`}
              onClick={() => setFilter("low")}
            >
              ⚠️ Low Score ({lowCandidates})
            </button>
          </div>
          <input
            type="text"
            placeholder="Search by candidate name, ID, or resume..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="admin-search"
          />
        </div>

        {/* Table */}
        <div className="admin-table-card">
          {loading ? (
            <div className="admin-loading">
              <div className="loading-spinner" />
              <p>Loading interviews...</p>
            </div>
          ) : filteredInterviews.length === 0 ? (
            <div className="admin-empty">
              <span className="empty-icon">📭</span>
              <p>No interviews found</p>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Position</th>
                  <th>Status</th>
                  <th>Resume Score</th>
                  <th>Interview Score</th>
                  <th>Final Score</th>
                  <th>Resume</th>
                  <th>Video</th>
                  <th>Excel Test</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInterviews.map((iv) => (
                  <tr key={iv.id} className="admin-row">
                    <td>
                      <div className="candidate-cell">
                        <div className="candidate-avatar">
                          {(iv.candidateName || iv.userId || "?")[0].toUpperCase()}
                        </div>
                        <div className="candidate-info" style={{ display: "flex", flexDirection: "column" }}>
                          <span className="candidate-name" style={{ fontWeight: "700", color: "#1a2332" }}>
                            {iv.candidateName || "Anonymous Candidate"}
                          </span>
                          <span className="candidate-id" title={iv.userId} style={{ fontSize: "11px", color: "#64748b" }}>
                            {iv.userId?.length > 20 ? iv.userId.slice(0, 20) + "..." : iv.userId}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      {iv.positionTitle ? (
                        <span style={{ padding: "3px 8px", borderRadius: "6px", background: "#ede9fe", color: "#6d28d9", fontSize: "0.75rem", fontWeight: 600 }}>
                          {iv.positionTitle}
                        </span>
                      ) : (
                        <span style={{ color: "#94a3b8", fontSize: "0.75rem" }}>—</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-pill ${iv.status || "completed"}`} style={{ 
                        padding: "4px 8px", 
                        borderRadius: "12px", 
                        fontSize: "0.75rem", 
                        fontWeight: "600",
                        backgroundColor: (iv.status === "completed" || !iv.status) ? "#d1fae5" : (iv.status === "active" ? "#fef3c7" : "#e0e7ff"),
                        color: (iv.status === "completed" || !iv.status) ? "#065f46" : (iv.status === "active" ? "#92400e" : "#4338ca"),
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px"
                      }}>
                        {iv.status === "active" && (
                          <span style={{ 
                            width: "8px", 
                            height: "8px", 
                            background: "#d97706", 
                            borderRadius: "50%", 
                            animation: "blink 1s infinite" 
                          }} />
                        )}
                        {iv.status === "completed" || !iv.status ? "Completed" : (iv.status === "active" ? "LIVE" : "In Progress")}
                      </span>
                    </td>
                    <td>
                      <span className="score-pill" style={{ backgroundColor: getScoreColor(iv.resumeScore || 0) }}>
                        {iv.resumeScore || 0}/10
                      </span>
                    </td>
                    <td>
                      <span className="score-pill" style={{ backgroundColor: getScoreColor(iv.interviewScore || 0) }}>
                        {iv.interviewScore || 0}/10
                      </span>
                    </td>
                    <td>
                      <div className="final-score-cell">
                        <span className="score-pill large" style={{ backgroundColor: getScoreColor(iv.finalScore || 0) }}>
                          {iv.finalScore || 0}/10
                        </span>
                        <span className="score-badge-text" style={{ color: getScoreColor(iv.finalScore || 0) }}>
                          {getScoreBadge(iv.finalScore || 0)}
                        </span>
                      </div>
                    </td>
                    <td>
                      {iv.resumeUrl ? (
                        <a href={iv.resumeUrl} target="_blank" rel="noopener noreferrer" className="score-pill" style={{ 
                          backgroundColor: "#4f46e5", 
                          color: "white", 
                          textDecoration: "none",
                          fontSize: "11px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px"
                        }}>
                          📄 View Resume
                        </a>
                      ) : (
                        <span className="no-data" style={{ color: "#94a3b8", fontSize: "11px" }}>No File</span>
                      )}
                    </td>
                    <td>
                      {iv.videoUrl ? (
                        <a href={iv.videoUrl} target="_blank" rel="noopener noreferrer" className="link-btn">
                          🎥 Watch
                        </a>
                      ) : (iv.status === "completed" && !iv.videoSynced) ? (
                        <span className="syncing-text" style={{ fontSize: "0.75rem", color: "#1d6fd8", fontStyle: "italic" }}>
                          ⏳ Syncing...
                        </span>
                      ) : (
                        <span className="no-data">—</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {iv.excelData ? (
                          <button onClick={() => handleDownloadExcel(iv)} style={{
                            padding: "4px 8px", backgroundColor: "#10b981", color: "white", border: "none", borderRadius: "4px", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px"
                          }}>
                            📊 Data File
                          </button>
                        ) : (
                          <span className="no-data" style={{ fontSize: "11px" }}>No Data</span>
                        )}
                        {iv.excelVideoUrl && (
                          <a href={iv.excelVideoUrl} target="_blank" rel="noopener noreferrer" style={{
                            padding: "4px 8px", backgroundColor: "#3b82f6", color: "white", borderRadius: "4px", fontSize: "11px", textDecoration: "none", display: "flex", alignItems: "center", gap: "4px"
                          }}>
                            📹 Screen Rec
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="date-cell">{formatDate(iv.completedAt || iv.startedAt)}</td>
                    <td>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button className="detail-btn" onClick={() => { discoverBase(); navigate(`/interview-detail/${iv.id}`); }}>
                          View Details
                        </button>
                        <button 
                          className="delete-btn" 
                          onClick={() => handleDelete(iv.id)}
                          style={{ 
                            padding: "6px 12px", 
                            background: "transparent", 
                            border: "1px solid #fee2e2", 
                            color: "#ef4444", 
                            borderRadius: "6px", 
                            fontSize: "12px", 
                            fontWeight: "600",
                            cursor: "pointer"
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
